/**
 * Prefab types for the Rivellum Console prefab catalog and configuration system.
 * Types align with the live `/prefabs` API response from the Rivellum node.
 */

import type { ApiPrefab, ApiAction, ApiConfigSchema, ApiPrefabCategory } from '../api/rivellumApi';

/** API categories as they come from the node */
export type PrefabCategory = ApiPrefabCategory;

export const PREFAB_CATEGORY_LABELS: Record<PrefabCategory, string> = {
  DEFI: 'DeFi',
  GAMING: 'Gaming',
  GOVERNANCE: 'Governance',
  MONETIZATION: 'Monetization',
  PAYMENTS: 'Payments',
};

/** UI widget type for rendering */
export type PrefabWidgetType = 'input' | 'number' | 'slider' | 'toggle' | 'select' | 'textarea' | 'assetPicker' | 'addressPicker' | 'table' | 'json';

/**
 * PrefabParam — derived from configSchema properties for form rendering.
 */
export interface PrefabParam {
  key: string;
  label: string;
  type: 'string' | 'number' | 'address' | 'boolean';
  required: boolean;
  description?: string;
  uiWidget?: PrefabWidgetType;
  enumValues?: (string | number)[];
  enumLabels?: string[];
  defaultValue?: string | number | boolean;
  minimum?: number;
  maximum?: number;
}

/**
 * PrefabDefinition — the shape stored in the Zustand catalog.
 * Directly maps from ApiPrefab with minimal transformation.
 */
export interface PrefabDefinition {
  id: string;
  name: string;
  category: PrefabCategory;
  version: string;
  kind: string;
  description: string;
  sourceFile: string;
  sourceHash: string;
  microSafe: boolean;
  casualSafe: boolean;
  actions: ApiAction[];
  configSchema: ApiConfigSchema;
  params: PrefabParam[];
  tags: string[];
}

export interface PrefabConfigValues {
  [key: string]: string;
}

export interface AddedPrefab {
  prefabId: string;
  configValues: PrefabConfigValues;
  addedAt: number;
}

/** Convert an ApiPrefab from the node into the frontend PrefabDefinition */
export function apiPrefabToDefinition(api: ApiPrefab): PrefabDefinition {
  // Derive params from configSchema.properties
  const params: PrefabParam[] = Object.entries(api.configSchema?.properties ?? {}).map(
    ([key, prop]) => ({
      key,
      label: prop.title || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      type: inferParamType(key, prop.type),
      required: (api.configSchema?.required ?? []).includes(key),
      description: prop.description,
      uiWidget: prop.uiWidget,
      enumValues: prop.enum,
      enumLabels: prop.enumLabels,
      defaultValue: prop.default,
      minimum: prop.minimum,
      maximum: prop.maximum,
    }),
  );

  return {
    id: api.id,
    name: api.name,
    category: api.category,
    version: String(api.version),
    kind: api.kind,
    description: api.description,
    sourceFile: api.sourceFile,
    sourceHash: api.sourceHash,
    microSafe: api.microSafe,
    casualSafe: api.casualSafe,
    actions: api.actions,
    configSchema: api.configSchema,
    params,
    tags: api.tags,
  };
}

function inferParamType(key: string, _jsonType: string): PrefabParam['type'] {
  const k = key.toLowerCase();
  if (k.includes('address') || k.includes('owner') || k.includes('recipient') || k.includes('player')) return 'address';
  if (k.includes('amount') || k.includes('supply') || k.includes('price') || k.includes('count')
    || k.includes('fee') || k.includes('threshold') || k.includes('limit') || k.includes('duration')
    || k.includes('period') || k.includes('interval') || k.includes('capacity') || k.includes('max')
    || k.includes('min') || k.includes('level') || k.includes('rate') || k.includes('weight')
    || k.includes('quorum') || k.includes('delay') || k.includes('required_count')) return 'number';
  if (k.includes('enabled') || k.includes('transferable') || k.includes('pausable') || k.includes('mintable') || k.includes('burnable')) return 'boolean';
  return 'string';
}
